import {
    FormatBoldRounded,
    FormatItalicRounded,
    FormatUnderlinedRounded,
    StrikethroughSRounded,
    FormatClearRounded,
    LinkRounded,
    FormatAlignLeftRounded,
    FormatAlignCenterRounded,
    FormatAlignRightRounded,
    FormatColorTextRounded,
} from "@mui/icons-material";
import { Divider, IconButton, Stack, Tooltip, Paper, Box, InputBase, alpha, useTheme, TextField, Portal } from "@mui/material";
import { useDispatch } from "react-redux";
import { useRef, useState, useEffect } from "react";
import { reduxHelper } from "../../../config/reduxHelper";
import { CheckRounded, CloseRounded, FormatSizeRounded, LinkRounded as LinkIcon } from "@mui/icons-material";

const Sep = () => (
    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />
);

export default function FloatingTextToolbar({
    selectionRect,
    absolute = true,
    linkMode: externalLinkMode,
    setLinkMode: setExternalLinkMode,
    url: externalUrl,
    setUrl: setExternalUrl
}) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colorInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const tempLinkHighlightRef = useRef(null);

    // Use external state if provided, otherwise fallback to local (for other callers)
    const [localLinkMode, setLocalLinkMode] = useState(false);
    const [localUrl, setLocalUrl] = useState("");

    const linkMode = externalLinkMode !== undefined ? externalLinkMode : localLinkMode;
    const setLinkMode = setExternalLinkMode !== undefined ? setExternalLinkMode : setLocalLinkMode;
    const url = externalUrl !== undefined ? externalUrl : localUrl;
    const setUrl = setExternalUrl !== undefined ? setExternalUrl : setLocalUrl;
    const [showFontSize, setShowFontSize] = useState(false);
    const [activeStyles, setActiveStyles] = useState({
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        justifyLeft: false,
        justifyCenter: false,
        justifyRight: false,
        link: false,
    });

    const refreshActiveStyles = () => {
        const safeState = (cmd) => {
            try {
                return document.queryCommandState(cmd);
            } catch (e) {
                return false;
            }
        };

        const selection = window.getSelection();
        let isLink = false;
        if (selection && selection.rangeCount > 0) {
            let container = selection.getRangeAt(0).commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;
            isLink = !!container.closest("a");
        }

        setActiveStyles({
            bold: safeState("bold"),
            italic: safeState("italic"),
            underline: safeState("underline"),
            strikeThrough: safeState("strikeThrough"),
            justifyLeft: safeState("justifyLeft"),
            justifyCenter: safeState("justifyCenter"),
            justifyRight: safeState("justifyRight"),
            link: isLink,
        });
    };

    useEffect(() => {
        if (linkMode) {
            // Preserve the original editor selection while typing into URL input.
            return;
        }
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const candidateRange = selection.getRangeAt(0);
            let container = candidateRange.commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;
            // Ignore selections outside editable content (e.g. URL input field).
            if (container?.closest?.('[contenteditable="true"]')) {
                savedRangeRef.current = candidateRange.cloneRange();
            }
        }
        refreshActiveStyles();
    }, [selectionRect, linkMode]);

    useEffect(() => {
        const onSelectionChange = () => {
            if (!selectionRect) return;
            refreshActiveStyles();
        };
        document.addEventListener("selectionchange", onSelectionChange);
        return () => document.removeEventListener("selectionchange", onSelectionChange);
    }, [selectionRect]);

    const withSavedSelection = (callback) => {
        const selection = window.getSelection();
        if (savedRangeRef.current && selection) {
            let container = savedRangeRef.current.commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;
            
            // Explicitly focus the editable parent to ensure execCommand targets it
            const editableParent = container.closest('[contenteditable="true"]');
            if (editableParent) {
                editableParent.focus();
            }

            selection.removeAllRanges();
            selection.addRange(savedRangeRef.current);
        }
        callback();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
    };

    const clearTempLinkHighlight = () => {
        // No-op: We no longer mutate the DOM destructively
    };

    const applyTempLinkHighlight = () => {
        // We rely on the native browser selection to indicate what's being linked.
        // Destructive DOM mutations like extractContents cause focus loss and editor closing.
    };

    useEffect(() => {
        if (!linkMode) {
            clearTempLinkHighlight();
        }
    }, [linkMode]);

    useEffect(() => () => {
        clearTempLinkHighlight();
    }, []);

    const handleFormat = (cmd, value = null) => {
        withSavedSelection(() => {
            document.execCommand(cmd, false, value);
        });
        setTimeout(refreshActiveStyles, 0);
    };

    const normalizeLinkUrl = (rawUrl = "") => {
        let val = String(rawUrl).trim().replace(/\s/g, "");
        if (!val) return "";
        // Keep internal anchors/routes and explicit schemes unchanged.
        if (val.startsWith("/") || val.startsWith("#") || /^[a-z][a-z0-9+.-]*:/i.test(val)) {
            return val;
        }
        return `https://${val}`;
    };

    const handleApplyLink = () => {
        const trimmedUrl = normalizeLinkUrl(url || "");
        if (trimmedUrl) {
            const selection = window.getSelection();
            if (selection && tempLinkHighlightRef.current) {
                const range = document.createRange();
                range.selectNodeContents(tempLinkHighlightRef.current);
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand("createLink", false, trimmedUrl);
            } else {
                handleFormat("createLink", trimmedUrl);
            }
        }
        setUrl(trimmedUrl);
        clearTempLinkHighlight();
        setLinkMode(false);
        setTimeout(refreshActiveStyles, 0);
    };

    const handleSetLinkMode = () => {
        setLinkMode(true);
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
            let container = selection.getRangeAt(0).commonAncestorContainer;
            if (container.nodeType === 3) container = container.parentNode;

            const link = container.closest("a");
            if (link) {
                setUrl(link.href);
            } else {
                setUrl("");
            }
        }
    };

    const ib = (title, Icon, onClick, active = false, isLink = false) => (
        <Tooltip title={title} key={title} placement="top">
            <IconButton
                size="small"
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick();
                }}
                onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                sx={{
                    color: active ? "primary.main" : "text.primary",
                    bgcolor: active ? "primary.lighter" : "transparent",
                    borderRadius: 1,
                    p: 0.4,
                    "&:hover": { bgcolor: active ? "primary.lighter" : "action.hover" },
                }}
            >
                <Icon sx={{ fontSize: 18 }} />
            </IconButton>
        </Tooltip>
    );

    if (!selectionRect && !linkMode) return null;

    return (
        <Paper
            elevation={8}
            onMouseDown={(e) => e.preventDefault()}
            onMouseUp={(e) => e.stopPropagation()}
            sx={{
                position: absolute ? 'fixed' : 'absolute',
                top: (() => {
                    const top = (absolute ? selectionRect?.top : (selectionRect?.top || 0)) || 0;
                    const height = selectionRect?.height || 20;
                    if (top < 80 && absolute) return top + height + 10;
                    return top - 60;
                })(),
                left: (absolute ? selectionRect?.left : (selectionRect?.left || 0)) + ((selectionRect?.width || 0) / 2),
                transform: 'translateX(-50%)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                p: 0.5,
                borderRadius: '50px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0px 10px 40px rgba(0,0,0,0.12)',
            }}
        >
            {linkMode ? (
                <div 
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '0 12px',
                        minWidth: '250px'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <input
                        autoFocus
                        type="text"
                        placeholder="Type URL here..."
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyLink()}
                        style={{
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '13px',
                            flex: 1,
                            outline: 'none',
                            color: '#333'
                        }}
                    />
                    <IconButton
                        size="small"
                        color="primary"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={handleApplyLink}
                        sx={{ ml: 1 }}
                    >
                        <CheckRounded fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={() => {
                            setLinkMode(false);
                            setTimeout(refreshActiveStyles, 0);
                        }}
                    >
                        <CloseRounded fontSize="small" />
                    </IconButton>
                </div>
            ) : showFontSize ? (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
                    {[1, 2, 3, 4, 5, 6, 7].map((size) => (
                        <IconButton
                            key={size}
                            size="small"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleFormat("fontSize", size);
                                setShowFontSize(false);
                            }}
                            sx={{ fontSize: '0.75rem', p: 0.5 }}
                        >
                            {size}
                        </IconButton>
                    ))}
                    <Sep />
                    <IconButton
                        size="small"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowFontSize(false);
                        }}
                    >
                        <CloseRounded fontSize="inherit" />
                    </IconButton>
                </Stack>
            ) : (
                <>
                    {/* Color Picker */}
                    <input
                        type="color"
                        ref={colorInputRef}
                        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                        onChange={(e) => handleFormat("foreColor", e.target.value)}
                    />
                    {ib("Text Color", FormatColorTextRounded, () => colorInputRef.current?.click())}

                    <Sep />

                    {/* Font Size */}
                    {ib("Font Size", FormatSizeRounded, () => setShowFontSize(true))}

                    <Sep />

                    {/* Basic Formatting Group */}
                    <Stack direction="row" spacing={0.25}>
                        {ib("Bold", FormatBoldRounded, () => handleFormat("bold"), activeStyles.bold)}
                        {ib("Italic", FormatItalicRounded, () => handleFormat("italic"), activeStyles.italic)}
                        {ib("Underline", FormatUnderlinedRounded, () => handleFormat("underline"), activeStyles.underline)}
                        {ib("Strikethrough", StrikethroughSRounded, () => handleFormat("strikeThrough"), activeStyles.strikeThrough)}
                        {ib("Clear Style", FormatClearRounded, () => handleFormat("removeFormat"))}
                    </Stack>

                    <Sep />

                    {/* Alignment Group */}
                    <Stack direction="row" spacing={0.25}>
                        {ib("Align Left", FormatAlignLeftRounded, () => handleFormat("justifyLeft"), activeStyles.justifyLeft)}
                        {ib("Align Center", FormatAlignCenterRounded, () => handleFormat("justifyCenter"), activeStyles.justifyCenter)}
                        {ib("Align Right", FormatAlignRightRounded, () => handleFormat("justifyRight"), activeStyles.justifyRight)}
                    </Stack>

                    <Sep />

                    {/* Link Group */}
                    {ib("Link", LinkRounded, handleSetLinkMode, activeStyles.link || linkMode)}
                </>
            )}
        </Paper>
    );
}
